import React, { useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import type {
    NodePosition, RenderLineDefault, RenderLine, RenderLineNode,
    DefaultNode, PaymentNode, MilestoneNode,
    RenderLineExpense, ExpenseNode
} from "./util"
import { lineNodeConfig, k_renderLineType } from "./util"

interface IProps{
    data?: RenderLine[]
}
const getNodeLineLinks = (data: RenderLine[]) => {
    if (!data) return []
    const links: NodePosition[] = []
    const allNodes: (RenderLineNode & { lineIndex: number })[] = []
    const nodeOffsetY = lineNodeConfig.lineHeight / 2
    data.forEach((line, lineIndex) => {
        line.renderNodes.forEach(node => {
            node.children.forEach(child => {
                allNodes.push({ ...child, nodeType: line.nodeType, lineIndex, percent: node.percent })
            })
        })
        
    })
    data.forEach((line, lineIndex) => {
        if (line.nodeType !== k_renderLineType.default && line.nodeType !== k_renderLineType.milestone && line.nodeType !== k_renderLineType.importMilestone) {
            return
        }
        (line as RenderLineDefault).renderNodes.forEach((renderNode, index) => {
            (renderNode.children as DefaultNode[]).forEach((startNode, index) => {
                const nodes = startNode.nodes as DefaultNode[]
                if (nodes && nodes.length > 0) {
                    nodes.forEach(linkNode => {
                        const endNode = allNodes.find(n => (n.nodeType === k_renderLineType.default || n.nodeType === k_renderLineType.milestone || n.nodeType === k_renderLineType.importMilestone) && (n as DefaultNode).ID === linkNode.ID)
                        if (endNode) {
                            links.push({
                                x1: renderNode.percent * 100 + "%",
                                y1: lineIndex * 100 + nodeOffsetY,
                                x2: endNode.percent * 100 + "%",
                                y2: endNode.lineIndex * 100 + nodeOffsetY,
                            })
                        }
                    })
                }
                const pmNodes = startNode.pmNodes
                if (pmNodes && pmNodes.length > 0) {
                    pmNodes.forEach(linkNode => {
                        let endNode
                        if (linkNode.type === k_renderLineType.milestone) {
                            endNode = allNodes.find(n => n.nodeType === k_renderLineType.milestone && (n as MilestoneNode).milestoneCode === linkNode.code)
                        } else if (linkNode.type === k_renderLineType.payment) {
                            endNode = allNodes.find(n => n.nodeType === k_renderLineType.payment && (n as PaymentNode).pmCode === linkNode.code)
                        }
                        if (endNode) {
                            links.push({
                                x1: renderNode.percent * 100 + "%",
                                y1: lineIndex * 100 + nodeOffsetY,
                                x2: endNode.percent * 100 + "%",
                                y2: endNode.lineIndex * 100 + nodeOffsetY,
                            })
                        }
                    })
                }
            })
            
        })
    })
    data.forEach((line, lineIndex) => {
        if (line.nodeType !== k_renderLineType.expense) {
            return
        }
        
        (line as RenderLineExpense).renderNodes.forEach((renderNode, index) => {
            (renderNode.children as ExpenseNode[]).forEach((startNode, index) => {
                const nodes = startNode.links
                if (nodes && nodes.length > 0) {
                    nodes.forEach(linkNode => {
                        const endNode = allNodes.find(n => n.nodeType === k_renderLineType.payment
                            && (n as PaymentNode).pmCode === linkNode.toCode);
                        if (endNode) {
                            // console.log(endNode, lineIndex, line)
                            links.push({
                                x1: renderNode.percent * 100 + "%",
                                y1: lineIndex * 100 + nodeOffsetY,
                                x2: endNode.percent * 100 + "%",
                                y2: endNode.lineIndex * 100 + nodeOffsetY,
                            })
                        }
                    })
                }
            })
            
        })
    })
    return links
}
const Index: React.FC<IProps> = (props) => {
    const [svgWidth, setSvgWidth] = useState(500)
    const svgRef = React.useRef<SVGSVGElement>(null);

    const linkList = useMemo(() => {
        return getNodeLineLinks(props.data || [])
    }, [props.data])

    useEffect(() => {
        if (!svgRef.current || !props.data) return
        // 2. 设置画布和布局
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove()

        // 添加箭头标记定义 - 虚线大于号形状
        svg.append('defs').append('marker')
            .attr('id', 'arrowhead')
            .attr('viewBox', '0 0 40 40')
            .attr('markerWidth', 16)
            .attr('markerHeight', 16)
            .attr('refX', 40) // 箭头位置，相对于路径终点
            .attr('refY', 20) // 箭头中心位置
            .attr('orient', 'auto') // 箭头自动朝向路径方向
            .append('path')
            .attr('d', 'M 0 10 L 40 20 L 0 30z') // 大于号形状
            .attr('fill', '#a1a1a1') // 无填充
            // .attr('stroke', '#a1a1a160') // 线条颜色
            .attr("stroke-dasharray", "5,5")
            .attr('stroke-width', 2) // 线条宽度
            .attr('stroke-linecap', 'round'); // 线条端点样式

        // 绘制连接线
        svg.selectAll('.link')
            .data(linkList)
            .enter()
            .append('line')
            .attr('class', 'link')
            .attr("x1", (d)=>d.x1)
            .attr("y1", (d) => d.y1)
            .attr("x2", (d) => d.x2) // 根据数据值确定长度
            .attr("y2", (d) => d.y2)
            .attr("stroke", "#16161680")
            .attr("stroke-width", 1)
            .attr("stroke-dasharray", "5,5")
            .attr("stroke-linecap", "round")
            .attr("stroke-linejoin", "round")
            .attr('marker-end', 'url(#arrowhead)'); // 添加箭头

    }, [svgRef, svgWidth, linkList])


    useEffect(() => {
        const resize = () => {
            if (!svgRef.current) return
            setSvgWidth(svgRef.current.parentElement?.clientWidth || 500)
        }
        resize()
        window.addEventListener("resize", resize)
        return () => {
            window.removeEventListener("resize", resize)
        }
    }, [setSvgWidth, svgRef])

    return (
        <svg ref={svgRef} className="absolute left-0 top-0 w-full h-full"></svg>
    )
}
export default Index